import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit, Signal } from '@angular/core';
import { ButtonModule, InputModule, SelectModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AffectedItemsFormGroup, OrderEntryDomesticFormGroup } from '../../../models/cod-create.model';
import { OrderLineTableComponent } from '../../../../shared/components/order-line-table/order-line-table.component';
import { IOrderLine, OrderEntryIssueTypeDomestic } from '@cod-monorepo/interfaces';
import { tap, startWith } from 'rxjs';
import { select } from '@ngxs/store';
import { CodState } from '../../../state/cod.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-cod-affected-items-order-entry-domestic-step',
    templateUrl: './affected-items-order-entry-domestic.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        OrderLineTableComponent
    ],
    standalone: true
})
export class CodAffectedItemsOrderEntryDomesticStepComponent implements OnInit {
    @Input({ required: true })
    affectedItemsForm: FormGroup<AffectedItemsFormGroup>;
    @Input({ required: true }) form: FormGroup<OrderEntryDomesticFormGroup>;

    public orderLineItems: Signal<IOrderLine[]> = select(CodState.orderLineItems);

    public issueTypes: string[] = [];
    public showOrderLines = true;
    public confirmed = false;
    public lineItems: IOrderLine[] = [];
    public disabled = false;
    public valid = false;

    private requiresOrderLines = [
        OrderEntryIssueTypeDomestic.MISSING_ITEMS_LINES_DOM,
        OrderEntryIssueTypeDomestic.P41_NOT_VALIDATED_DOM,
        OrderEntryIssueTypeDomestic.WRONG_MATERIAL_DOM,
        OrderEntryIssueTypeDomestic.PRICE_OVERRIDE_DISCOUNTED_ORDERS,
        OrderEntryIssueTypeDomestic.POC_PRICE_PROMO,
        OrderEntryIssueTypeDomestic.EXPEDITE_FEE_WAIVED,
        OrderEntryIssueTypeDomestic.CUSTOMER_ORDERED_WRONG_MATERIAL_DOM,
        OrderEntryIssueTypeDomestic.EDI_ORDERING_ISSUE_DOM,
        OrderEntryIssueTypeDomestic.MISINFORMATION_PROVIDED_COMMUNICATION_INADEQUATE_DOM,
        OrderEntryIssueTypeDomestic.CUSTOMER_ORDER_ERROR_DOM
    ];

    private hiddenIssueTypes = [
        OrderEntryIssueTypeDomestic.WRONG_BILL_TO_SHIP_TO_ACCOUNT_DOM,
        OrderEntryIssueTypeDomestic.PLANNER_ERROR_DOM,
        OrderEntryIssueTypeDomestic.REFUSED_SHIPMENT_DOM,
        OrderEntryIssueTypeDomestic.CUSTOMER_ORDERED_WRONG_MATERIAL_DOM
    ];

    private destroyRef = inject(DestroyRef);

    public ngOnInit(): void {
        this.initIssueTypes();
        this.initItemTypeChangeListener();
        this.initStateListeners();
    }

    public setConfirmed(): void {
        const issueType = this.form.controls.issueType.value;

        if (!this.requiresOrderLines.includes(issueType as OrderEntryIssueTypeDomestic)) {
            this.affectedItemsForm.controls.affectedOrderLines.setValue(
                this.orderLineItems().map(line => ({ ...line, affectedQuantity: line.salesOrderQuantity }))
            );
        }

        this.affectedItemsForm.controls.confirmed.setValue(true);
    }

    public handleSelectionChange(value: IOrderLine[]): void {
        this.affectedItemsForm.controls.confirmed.setValue(false);
        this.affectedItemsForm.get('affectedOrderLines')?.setValue(value);
    }

    private initStateListeners(): void {
        // Listen to disabled state changes
        this.affectedItemsForm.statusChanges
            .pipe(
                startWith(this.affectedItemsForm.status),
                tap(() => {
                    this.disabled = this.affectedItemsForm.disabled;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // Listen to confirmed state changes
        this.affectedItemsForm.controls.confirmed.valueChanges
            .pipe(
                startWith(this.affectedItemsForm.controls.confirmed.value),
                tap(value => {
                    this.confirmed = value ?? false;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // Listen to affected order lines changes
        this.affectedItemsForm.controls.affectedOrderLines.valueChanges
            .pipe(
                startWith(this.affectedItemsForm.controls.affectedOrderLines.value),
                tap(value => {
                    this.lineItems = value ?? [];
                    this.updateValid();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // Listen to issue type changes for validation
        this.form.controls.issueType.valueChanges
            .pipe(
                startWith(this.form.controls.issueType.value),
                tap(() => {
                    this.updateValid();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private updateValid(): void {
        const type = this.form.controls.issueType.value;
        const orderLinesSelected = (this.affectedItemsForm.controls.affectedOrderLines.value ?? []).length > 0;

        this.valid = !!type && this.requiresOrderLines.includes(type) ? orderLinesSelected : true;
    }

    private initItemTypeChangeListener(): void {
        this.form.controls.issueType.valueChanges
            .pipe(
                tap(value => {
                    this.affectedItemsForm.controls.typeValid.setValue(!!value);
                    this.showOrderLines = this.requiresOrderLines.includes(value as OrderEntryIssueTypeDomestic);
                })
            )
            .subscribe();
    }

    private initIssueTypes(): void {
        if (this.disabled) {
            this.issueTypes = Object.values(OrderEntryIssueTypeDomestic);
        } else {
            this.issueTypes = Object.values(OrderEntryIssueTypeDomestic).filter(
                type => !this.hiddenIssueTypes.includes(type)
            );
        }
    }
}
